import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { ListAdmins } from '@cold-ambulance/client';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@cold-ambulance/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import {
  createdAtColumn,
  emailColumn,
  fullNameColumn,
  updatedAtColumn,
} from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { CreateAdmin } from './create-admin.jsx';
import { UpdateAdminDialog } from './update-admin.tsx';

export default function AdminsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const deleteAdminMutation = useAction('DELETE /admin/{id}', {
    invalidate: ['GET /admins'],
    meta: { toast: true },
    mutationFn: (dispatch) => dispatch({ id: deleteTarget!.id }),
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('Admin deleted');
    },
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<
    ListAdmins['records'][number] | null
  >(null);

  const columns: ColumnDef<ListAdmins['records'][number]>[] = [
    emailColumn(),
    fullNameColumn(),
    createdAtColumn(),
    updatedAtColumn(),
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const it = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <UpdateAdminDialog
                admin={it}
                onSave={() => dataQuery.refetch()}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    Edit admin
                  </DropdownMenuItem>
                }
              />
              <DropdownMenuItem
                className="text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setDeleteTarget(it);
                }}
              >
                Delete admin
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /admins',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      query: searchQuery || undefined,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as 'email' | 'firstName' | 'lastName' | 'createdAt')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Admins"
        totalCount={`${dataQuery.data?.totalCount || 0} total admins`}
        icon={Users}
      >
        <CreateAdmin
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add Admin
            </Button>
          }
          onSave={() => dataQuery.refetch()}
        />
      </CountablePageHeader>

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        mutation={deleteAdminMutation}
        title="Delete admin"
        description={
          deleteTarget ? (
            <span>
              Are you sure you want to delete the admin{' '}
              <strong>
                {deleteTarget.firstName} {deleteTarget.lastName}
              </strong>
              ? This action cannot be undone.
            </span>
          ) : (
            ''
          )
        }
      />
    </div>
  );
}
